import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationDialog } from 'Common/ConfirmationDialog/ConfirmationDialog';
import {
  connectToFreshStartDesktop,
  saveIsFreshStartMachineReadyToClone,
  selectFreshStartDesktopLink,
  selectIsFreshStartDesktopDeletePending,
  selectIsFreshStartDesktopStartPending,
  selectIsFreshStartDesktopStopPending
} from 'Courses/data';
import { useAppDispatch, useAppSelector } from 'Store';
import { useBoolean } from 'Utils/customHooks';
import { openExternalUrl } from 'Utils/redirects';

import type { ISaveIsFreshStartMachineReadyToCloneRequest } from '@libs/models';

import { ConnectToDesktopButton } from './ConnectToDesktopBase';

type Props = {
  courseId: string;
  isFreshStartMachineReadyToClone?: boolean;
  disabled?: boolean;
};

export const ConnectToFreshStartDesktopButton = ({
  courseId,
  isFreshStartMachineReadyToClone,
  disabled
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [
    connectToFreshStartDialogIsDisplayed,
    displayConnectToFreshStartDialog,
    hideConnectToFreshStartDialog
  ] = useBoolean();

  const isFreshStartDesktopStartPending = useAppSelector(state =>
    selectIsFreshStartDesktopStartPending(state, courseId)
  );

  const isFreshStartDesktopStopPending = useAppSelector(state =>
    selectIsFreshStartDesktopStopPending(state, courseId)
  );

  const isFreshStartDesktopDeletePending = useAppSelector(state =>
    selectIsFreshStartDesktopDeletePending(state, courseId)
  );

  const freshStartDesktopLink = useAppSelector(state =>
    selectFreshStartDesktopLink(state, courseId)
  );

  const redirectToDesktop = (desktopLink: string) => openExternalUrl(desktopLink);

  const shouldNotRedirectToNewTab = true;

  const connectToDesktop = () =>
    dispatch(connectToFreshStartDesktop(courseId)).then(() => {
      !shouldNotRedirectToNewTab &&
        freshStartDesktopLink &&
        redirectToDesktop(freshStartDesktopLink);
    });

  const handleConnect = () => {
    connectToDesktop();

    dispatch(
      saveIsFreshStartMachineReadyToClone({
        courseId,
        isFreshStartMachineReadyToClone: false
      } as ISaveIsFreshStartMachineReadyToCloneRequest)
    );

    hideConnectToFreshStartDialog();
  };

  const onConnectToDesktopClick = () => {
    if (isFreshStartMachineReadyToClone) {
      displayConnectToFreshStartDialog();
    } else {
      handleConnect();
    }
  };

  return (
    <>
      <ConnectToDesktopButton
        buttonText={t('Connect')}
        onConnectToDesktopClick={onConnectToDesktopClick}
        disabled={disabled}
        isDesktopStartPending={isFreshStartDesktopStartPending}
        isDesktopStopPending={isFreshStartDesktopStopPending}
        isDesktopDeletePending={isFreshStartDesktopDeletePending}
        isFreshStart
      />

      <ConfirmationDialog
        open={connectToFreshStartDialogIsDisplayed}
        content={`${t('ConnectToFreshStartWarning')} ${t('AreYouSure')}`}
        onNo={hideConnectToFreshStartDialog}
        onYes={handleConnect}
      />
    </>
  );
};
